/**
 * The one set of row words for bank lines and wallet payments (rd Y1).
 *
 * "Booked" is a claim about the books: the owner's rule (2026-10-27) is that a
 * bank line says Booked ONLY when the loaded books copy proves a booking on
 * the row (`item.booking`). Otherwise the row says Processed, the Home word.
 * Home (Y7) imports this too, so keep it pure and typed on the existing item.
 *
 * The other four words are the status filter names. A filter name must equal
 * the word its rows show, so they live here rather than in two places.
 */

import type { ActivityItem } from "../console/types.js";
import { moreText } from "./display-words.js";

export const ACTIVITY_STATE_WORDS = {
  needs_you: "Needs you",
  received: "Waiting",
  approved: "Waiting to book",
  dismissed: "Dismissed",
  with_the_owner: "With the owner",
} as const;

export const BOOKED_WORD = "Booked";
export const PROCESSED_WORD = "Processed";

/**
 * A Processed row with no booking of its own says what handled it (rd L2
 * review round 1). The row's status word stays "Processed" so the status filter
 * and its rows agree; this line sits beside it and never suggests the money is
 * missing — a line the wallet pass consumed is in the books through the wallet
 * entry, and a removed line was never booked at all.
 */
export const PROCESSED_ROW_NOTE = "Handled, no entry of its own";

/**
 * The word on a row. `booked` and `processed` are both "did this money reach
 * the books?" states; only a proven booking makes either one Booked.
 */
export function bankLineStatusWord(item: ActivityItem): string {
  if (item.state === "booked" || item.state === "processed") {
    return item.booking === undefined ? PROCESSED_WORD : BOOKED_WORD;
  }
  return ACTIVITY_STATE_WORDS[item.state];
}

/**
 * The word for a wallet movement's kind (rd L2 review round 1).
 *
 * The Worker sets `moov.kind` to either a tenant-breakdown kind (`rent`,
 * `late_fee`, …) or the raw wallet transaction type (`ach-credit`, …):
 * `ingest-worker/src/activity.ts` `moovDetails` picks the first breakdown kind
 * with money in it and falls back to `row.transaction_type`. `tests` keeps
 * this table equal to that file's `KIND_LABEL` — the console cannot import
 * Worker code, so the drift test reads both lists and fails when they differ.
 * A kind nobody listed reads as the one true thing this page knows: it was a
 * wallet payment.
 */
export const WALLET_BREAKDOWN_KIND_WORDS: Readonly<Record<string, string>> = {
  rent: "Rent",
  late_fee: "Late fee",
  pet_fee_recurring: "Pet fee",
  pet_fee_one_time: "Pet fee",
  water_sewer: "Water and sewer",
  gas_electric: "Gas and electric",
  wifi: "Wifi",
  tenant_damage: "Damage",
  lease_violation_fee: "Lease violation fee",
  parking: "Parking",
  storage: "Storage",
  nsf_fee: "NSF fee",
  application_fee: "Application fee",
  early_termination_fee: "Early termination fee",
  renters_insurance: "Renters insurance",
  lockout_fee: "Lockout fee",
  mailing_fee: "Mailing fee",
  cleaning_fee: "Cleaning fee",
  repairs_reimbursement: "Repairs",
  credit_card_fee: "Card fee",
  other_fee: "Other fee",
  prepaid: "Prepaid",
  security_deposit: "Security deposit",
  pet_deposit: "Pet deposit",
};

/**
 * The wallet transaction types a row can carry, in words. Moov's own list is
 * open (the column is any short lowercase word), so this is not exhaustive:
 * anything absent reads as "Wallet payment" rather than a slug on screen.
 */
const WALLET_TYPE_WORDS: Readonly<Record<string, string>> = {
  "ach-credit": "Bank transfer in",
  "ach-debit": "Bank transfer out",
  "card-payment": "Card payment",
  payout: "Payout",
  "moov-fee": "Processor fee",
  // The demo's own wallet rows, so the demo does not read as a guess.
  tenant_payment: "Tenant payment",
};

/** One plain word for a stored `moov.kind` value. */
export function walletKindWord(kind: string): string {
  return WALLET_BREAKDOWN_KIND_WORDS[kind] ?? WALLET_TYPE_WORDS[kind] ?? "Wallet payment";
}

/**
 * How many items of a wallet description are printed before the rest are
 * counted as "and N more" (rd16 U, C7). A description that lists twenty
 * breakdown kinds ("Pet fee, Pet fee, Rent, …") made the row unreadable and
 * repeated words; four says what the payment was without becoming the row.
 */
export const ACTIVITY_DESCRIPTION_MAX_ITEMS = 4;

/**
 * A row's description, as one readable line (rd16 U, C7).
 *
 * THE DEFECT. The Worker builds a wallet row's description as its breakdown
 * labels joined by ", " (`ingest-worker/src/activity.ts` `moovRowCandidate`),
 * and two kinds share one label (`pet_fee_recurring` and `pet_fee_one_time`
 * are both "Pet fee"), so a two-pet payment read "Pet fee, Pet fee" — and a
 * payment with many labelled amounts ran to twenty-odd items. A movement with
 * no breakdown labels reads `Wallet ${transaction_type}`, which gave
 * "Wallet wallet-transfer" (a stutter and a hyphenated code, not words).
 *
 * WHAT A WALLET ROW GETS. The list is split on its own separators, an item
 * that repeats an earlier one EXACTLY is said once, a stored kind is read as
 * words ("Wallet wallet-transfer" -> "Wallet transfer"), and the line is capped
 * at {@link ACTIVITY_DESCRIPTION_MAX_ITEMS} with the shared `moreText` tail.
 * The count is of the items THIS LINE leaves out of one description string the
 * row already holds in full — not of a list read from the server.
 *
 * WHAT A BANK ROW GETS, AND WHY IT IS ALMOST NOTHING. A bank line's description
 * is a merchant name or a descriptor: the bank's own text. Reading a hyphen as
 * a space or collapsing repeated words turned "co-op e-commerce" into
 * "co op e commerce" (rd16 U review, item 7), so neither repair is applied
 * here. The ONE thing dropped is an item that repeats an earlier item EXACTLY
 * and case-sensitively ("Fee, Fee" yes; "Fee, fee" no — two different items as
 * far as the console can tell). When nothing repeats, the text comes back
 * byte-for-byte: whitespace is not collapsed either, because a descriptor's
 * own spacing is the bank's text (rd16 U review round 2, item 7).
 */
export function activityDescriptionText(
  raw: string,
  opts: { wallet: boolean },
): string {
  if (raw === "") return "";
  if (!opts.wallet) return dedupeExactItems(raw);
  const items = splitItems(raw);
  const walletItems = items.map(walletKindPhrase);
  const shown = walletItems.slice(0, ACTIVITY_DESCRIPTION_MAX_ITEMS);
  const rest = walletItems.length - shown.length;
  const line = shown.join(", ");
  return rest > 0 ? `${line} and ${moreText(rest, true)}` : line;
}

/**
 * A bank row's own text, with an item that repeats an earlier item EXACTLY
 * dropped (rd16 U review round 2, item 7).
 *
 * `raw.split(",")` keeps each part's own spacing, so `parts.join(",")`
 * reconstructs the original text exactly; when nothing repeated, `raw` is
 * returned untouched (no trim, no whitespace collapse).
 */
function dedupeExactItems(raw: string): string {
  const parts = raw.split(",");
  const seen = new Set<string>();
  const kept: string[] = [];
  let dropped = false;
  for (const part of parts) {
    const item = part.trim();
    if (item === "") {
      kept.push(part);
      continue;
    }
    if (seen.has(item)) {
      dropped = true;
      continue;
    }
    seen.add(item);
    kept.push(part);
  }
  return dropped ? kept.join(",") : raw;
}

/**
 * The items of a wallet row's description, in order, with an exact repeat said
 * once. Whitespace is normalised here because this text is the Worker's own
 * joined list, not a bank's.
 */
function splitItems(raw: string): string[] {
  const text = raw.replace(/\s+/g, " ").trim();
  const items: string[] = [];
  const seen = new Set<string>();
  for (const part of text.split(",")) {
    const item = part.trim();
    if (item === "" || seen.has(item)) continue;
    seen.add(item);
    items.push(item);
  }
  return items;
}

/**
 * "Wallet wallet-transfer" -> "Wallet transfer" (rd16 U, C7).
 *
 * The Worker's fallback description is `Wallet ${row.transaction_type}`
 * (`ingest-worker/src/activity.ts`), where `transaction_type` is a lowercase
 * hyphenated kind (`wallet-transfer`, `ach-credit`). This turns that ONE shape
 * into words and drops the stutter; any other wallet item (a breakdown label
 * such as "Pet fee") is returned as it is. It is applied to wallet rows only,
 * so a bank descriptor is never re-spaced.
 */
function walletKindPhrase(item: string): string {
  const match = /^Wallet\s+([A-Za-z0-9-]+)$/.exec(item);
  const kind = match?.[1] ?? "";
  if (!/^[a-z][a-z0-9]*(?:-[a-z][a-z0-9]*)+$/.test(kind)) return item;
  const words = kind.split("-");
  const first = words[0];
  const rest = first !== undefined && first.toLowerCase() === "wallet" ? words.slice(1) : words;
  return ["Wallet", ...rest].join(" ");
}

/**
 * True for a value that is filler, not a name (rd16 U, C7).
 *
 * WHAT THE DATA IS. The sandbox company's wallet rows carried a property and a
 * lease made only of `x` characters. Nothing invented them on the console
 * side: the Worker copies the hub/portal `property` tag and the `lease_number`
 * column, and its only gate is length (and `[A-Z0-9-]{3,40}` for the lease),
 * which `xxxxxxx` passes. So the console cannot fix the data — but it must not
 * print filler as if it were a real address or lease either.
 *
 * The test is deliberately narrow: at least three alphanumeric characters, and
 * EVERY one of them the same character (ignoring spaces and punctuation). A
 * real name never looks like that ("123 Test Street", "TEST-26-0001"), and a
 * two-character run ("AA") is left alone because it can be a real abbreviation.
 */
export function isFillerValue(value: string): boolean {
  const compact = value.replace(/\s+/g, "");
  if (compact.length < 3) return false;
  const alphanumeric = compact.replace(/[^A-Za-z0-9]/g, "");
  // Nothing but punctuation: "....", "----" — never a name.
  if (alphanumeric.length === 0) return true;
  if (alphanumeric.length < 3) return false;
  const first = alphanumeric[0];
  for (const character of alphanumeric) {
    if (character.toLowerCase() !== first?.toLowerCase()) return false;
  }
  return true;
}

/** A stored value only when it is a real name, never filler. */
export function realValueText(value: string | undefined | null): string {
  if (value === undefined || value === null) return "";
  const text = value.trim();
  return text === "" || isFillerValue(text) ? "" : text;
}

